import { type CommandProps, Mark, mergeAttributes } from '@tiptap/core';
import type { EditorState, Transaction } from '@tiptap/pm/state';

export interface UppercaseMarkOptions {
  HTMLAttributes: Record<string, any>;
}

const changeSelectedTextCase = (state: EditorState, tr: Transaction, changeCase: (text: string) => string) => {
  state.selection.ranges.forEach(({ $from: { pos: from }, $to: { pos: to } }) => {
    state.doc.nodesBetween(from, to, (node, pos) => {
      if (!node.isText) return;

      const start = Math.max(from, pos);
      const end = Math.min(to, pos + node.nodeSize);
      const text = node.text!.slice(start - pos, end - pos);
      const changed = changeCase(text);
      if (changed === text) return;

      tr.replaceWith(tr.mapping.map(start), tr.mapping.map(end), state.schema.text(changed, node.marks));
    });
  });
};

export const UppercaseMark = Mark.create<UppercaseMarkOptions>({
  name: 'uppercase',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  parseHTML() {
    return [
      {
        style: 'text-transform',
        getAttrs: (value: string | HTMLElement) => typeof value === 'string' && /^(uppercase)$/.test(value) && null,
      },
    ];
  },

  renderHTML({ HTMLAttributes }: any) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, {
        ...HTMLAttributes,
        style: 'text-transform: uppercase;',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      toggleUppercase:
        () =>
        ({ commands, state, tr, dispatch }: CommandProps) => {
          if (dispatch) changeSelectedTextCase(state, tr, (text) => text.toUpperCase());
          return commands.setMark('uppercase');
        },
      toggleLowercase:
        () =>
        ({ commands, state, tr, dispatch }: CommandProps) => {
          if (dispatch) changeSelectedTextCase(state, tr, (text) => text.toLowerCase());
          return commands.unsetMark('uppercase');
        },
    };
  },
});
